import { Bell, Check, Clock, Mail, Trash2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNotifications, Notification } from "@/hooks/useNotifications";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useOrganizations } from "@/hooks/useOrganizations";

export function NotificationDropdown() {
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearAllNotifications } = useNotifications();
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const { toast } = useToast();
  const { data: organizations } = useOrganizations();
  const [open, setOpen] = useState(false);

  const handleNotificationClick = async (notification: typeof notifications[0]) => {
    // Close menu immediately to avoid overlay blocking UI
    setOpen(false);
    // Mark as read in the background (don't block UI)
    markAsRead(notification.id).catch(() => {});

    const meta = (notification as any)?.metadata || {};
    const taskId: string | undefined = (meta.taskId || meta.task_id || meta.queue_item_id || meta.id) as string | undefined;
    const queueId: string | undefined = (meta.queueId || meta.queue_id) as string | undefined;

    // If this is a task notification, check if the task still exists
    if (taskId && notification.type === 'task') {
      try {
        const { data: taskExists, error } = await supabase
          .from('queue_items')
          .select('id')
          .eq('id', taskId)
          .maybeSingle();
        
        if (error || !taskExists) {
          toast({
            title: "Task Deleted",
            description: "This task has been deleted and is no longer available.",
            variant: "destructive",
          });
          return;
        }
      } catch (error) {
        console.error('Error checking if task exists:', error);
        toast({
          title: "Error",
          description: "Unable to verify if this task still exists.",
          variant: "destructive",
        });
        return;
      }
    }

    // Resolve organization context robustly so links never 404
    const resolveOrgId = async (): Promise<string | null> => {
      // 1) From current route
      if (organizationId) return organizationId;
      // 2) From current path
      const pathOrgMatch = window.location.pathname.match(/\/org\/([^/]+)/);
      if (pathOrgMatch) return pathOrgMatch[1];
      // 3) From notification metadata
      const metaOrgId = (notification as any)?.metadata?.organizationId as string | undefined;
      if (metaOrgId) return metaOrgId;
      // 4) From task -> look up queue's org
      if (taskId) {
        try {
          const { data, error } = await supabase
            .from('queue_items')
            .select('id, queue_id, queues!inner(organization_id)')
            .eq('id', taskId)
            .maybeSingle();
          if (!error && data && (data as any).queues?.organization_id) {
            return (data as any).queues.organization_id as string;
          }
        } catch (_) {
          // ignore
        }
      }
      // 5) Fallback to user's first org
      if (organizations && organizations.length > 0) return organizations[0].id;
      return null;
    };

    const orgId = await resolveOrgId();
    if (!orgId) {
      toast({
        title: "Missing organization context",
        description: "We couldn't determine which organization to open.",
        variant: "destructive",
      });
      return;
    }

    let targetUrl = notification.action_url || '';

    // If it's a task notification or we can infer a task, close sidebars immediately
    if (taskId || notification.type === 'task') {
      sessionStorage.setItem('closeSidebars', '1');
      window.dispatchEvent(new CustomEvent('closeAllSidebars'));
    }

    // Navigate to queue and highlight task
    if (taskId) {
      // Store context for the Queues page
      sessionStorage.setItem('highlightTaskId', taskId as string);
      if (queueId) sessionStorage.setItem('targetQueueId', queueId);

      // Fire immediate highlight event for same-page interactions
      window.dispatchEvent(new CustomEvent('queue:highlightTask', { detail: { taskId, queueId } }));

      const queueParam = queueId ? `?queue=${queueId}` : '';
      targetUrl = `/dashboard/org/${orgId}/queues${queueParam}`;
    } else {
      // Normalize other routes to include organization context
      if (targetUrl.startsWith('/dashboard/org/')) {
        // already org-scoped
      } else if (targetUrl.startsWith('/dashboard/')) {
        const routePart = targetUrl.replace('/dashboard/', '');
        targetUrl = `/dashboard/org/${orgId}/${routePart}`;
      } else if (targetUrl.startsWith('/queues') || targetUrl.startsWith('/dashboard/queues')) {
        targetUrl = `/dashboard/org/${orgId}/queues`;
      } else if (!targetUrl) {
        targetUrl = `/dashboard/org/${orgId}/queues`;
      }
    }
    setTimeout(() => navigate(targetUrl), 100);
  };

  const formatTimestamp = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minutes ago`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'invite':
        return <Mail className="h-4 w-4 text-blue-500" />;
      case 'task':
        return <Check className="h-4 w-4 text-green-500" />;
      case 'queue':
        return <Clock className="h-4 w-4 text-orange-500" />;
      case 'system':
        return <Bell className="h-4 w-4 text-purple-500" />;
      default:
        return <Bell className="h-4 w-4 text-muted-foreground" />;
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="relative h-8 w-8 rounded-full">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 text-xs flex items-center justify-center bg-primary text-primary-foreground"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80 max-h-96 bg-popover z-[100] shadow-lg" align="end">
        <div className="flex items-center justify-between p-2">
          <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-auto p-1 text-xs text-muted-foreground hover:text-foreground"
                onClick={markAllAsRead}
              >
                Mark all read
              </Button>
            )}
            {notifications.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-auto p-1 text-xs text-muted-foreground hover:text-destructive"
                onClick={async () => {
                  await clearAllNotifications();
                  toast({ title: "Cleared", description: "All notifications deleted" });
                }}
                title="Clear all notifications"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>
        <DropdownMenuSeparator />
        
        <ScrollArea className="h-72">
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No notifications</p>
            </div>
          ) : (
            <div className="space-y-1">
              {notifications.map((notification) => (
                <DropdownMenuItem
                  key={notification.id}
                  className={`p-3 cursor-pointer focus:bg-muted ${
                    !notification.read ? 'bg-muted/50' : ''
                  }`}
                  onSelect={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start space-x-3 w-full">
                    <div className="flex-shrink-0 mt-0.5">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className={`text-sm font-medium truncate ${
                          !notification.read ? 'text-foreground' : 'text-muted-foreground'
                        }`}>
                          {notification.title}
                        </p>
                        {!notification.read && (
                          <div className="h-2 w-2 bg-primary rounded-full flex-shrink-0 ml-2" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatTimestamp(notification.created_at)}
                      </p>
                    </div>
                  </div>
                </DropdownMenuItem>
              ))}
            </div>
          )}
        </ScrollArea>
        
        {notifications.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-center justify-center p-2 text-sm text-muted-foreground hover:text-foreground">
              View all notifications
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
